/* eslint-disable no-restricted-syntax, no-restricted-imports, import/no-extraneous-dependencies -- File defines overrides */
import React from 'react';

import { Link as BricksLink, LinkProps as BricksLinkProps, Icon } from '@opendoor/bricks/core';
import { bricks, BricksComponent } from '@opendoor/bricks/system';
import { useMultiStyleConfig } from '@opendoor/bricks/system/utils';
import { Link as BaseRouterLink, LinkProps as ReactRouterLinkProps } from '@remix-run/react';

import { useObservability } from 'platform/observability';

import { trackNavigationEvent } from 'shared/helpers/hooks/useTrackPerformance';

import { useToPath } from '../preserveQueryParams';
import { useStalePageCheck } from '../useReloadStalePage';

/** @deprecated */
export type LegacyExternalLinkProps = BricksLinkProps;

/**
 * Styled link component that is used to navigate to a route EXTERNAL to the SPA
 * @warning `onClick` should not be used to navigate to a route! Use `href` instead.
 * @deprecated This component uses Bricks styles. Please consult your design and use the bricks-next `Link` component in `ExternalLink` instead.
 */
export const LegacyExternalLink: React.FC<LegacyExternalLinkProps> = ({
  analyticsName,
  children,
  variant,
  ...props
}) => (
  <BricksLink
    analyticsName={analyticsName}
    variant={props.leftIcon || props.rightIcon ? 'nav' : variant}
    {...props}
  >
    {children}
  </BricksLink>
);

const StyledSystemRouterLink = bricks(BaseRouterLink) as BricksComponent<'a'>;
type RouterLinkProps = Omit<ReactRouterLinkProps, 'component' | keyof BricksLinkProps> &
  React.RefAttributes<HTMLAnchorElement> &
  // Internal links use the react-router-dom `to` prop
  Omit<BricksLinkProps, 'href' | 'leftIcon' | 'rightIcon'> & {
    to: React.ComponentProps<typeof BaseRouterLink>['to'];
    customDisallowedSearchParams?: string[];
  };

/**
 * Styled React Router DOM Link component. Used to improve performance when navigating internal to the SPA
 */
const RouterLink: React.FC<RouterLinkProps> = ({
  children,
  to,
  onClick,
  'aria-label': ariaLabel,
  analyticsName,
  variant,
  customDisallowedSearchParams,
  ...props
}) => {
  const { trackJourneyEvent } = useObservability();
  const getToPath = useToPath(customDisallowedSearchParams);
  const toPath = getToPath(to);
  const checkStalePageAndAuth = useStalePageCheck();

  const onClickHandler = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    checkStalePageAndAuth();
    trackNavigationEvent();

    trackJourneyEvent('platform', 'linkClickEvent', { targetName: analyticsName });
    // The `onClick` prop should not be doing any navigation so it's safe to call it before the redirect
    onClick && onClick(e);
  };

  return (
    <StyledSystemRouterLink
      to={toPath}
      onClick={onClickHandler}
      aria-label={ariaLabel ?? undefined}
      {...props}
    >
      {children}
    </StyledSystemRouterLink>
  );
};

export type LegacyInternalLinkProps = RouterLinkProps &
  Partial<Pick<BricksLinkProps, 'leftIcon' | 'rightIcon'>>;

/**
 * Styled React Router DOM Link component. Used to improve performance when navigating internal to the SPA
 *
 * @deprecated This component uses Bricks styles. Please consult your design and use the bricks-next `Link` component in `InternalLink` instead.
 */
export const LegacyInternalLink: React.FC<LegacyInternalLinkProps> = ({
  children,
  leftIcon,
  rightIcon,
  variant,
  ...props
}) => {
  // Defaults to DEPRECATED primary link styles
  const variantStyles = useMultiStyleConfig('Link', variant ? { variant } : { variant: 'primary' });

  return (
    <RouterLink __css={variantStyles.link} {...props}>
      {leftIcon && <Icon {...leftIcon} aria-hidden />}
      {children}
      {rightIcon && <Icon {...rightIcon} aria-hidden />}
    </RouterLink>
  );
};
